import { useState, type ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  applicationSchema,
  type ApplicationInput,
  type WorkerDocumentInput,
} from '../domain/applicationSchema';
import { BANCOS, BANCO_OTRO, esBancoDeLaLista } from '../domain/bancos';
import {
  documentStatus,
  submitApplication,
  uploadApplicationDocument,
  uploadApplicationFile,
  AlreadyRegisteredError,
  BlockedDocumentError,
  DuplicatePendingError,
} from '../api/applications';
import { archivoIlegible } from '../lib/archivoLegible';
import { rechazoPorTamano, rechazoPorTipo } from '../lib/archivoDeFoto';

const EMPTY_FORM: ApplicationInput = {
  documentType: 'DNI',
  dni: '',
  firstNames: '',
  lastNames: '',
  phone: '',
  email: '',
  birthDate: '',
  department: '',
  province: '',
  district: '',
  address: '',
  bank: '',
  accountNumber: '',
  cci: '',
};

const STEP_FIELDS: Record<1 | 2, (keyof ApplicationInput)[]> = {
  1: [
    'documentType',
    'dni',
    'firstNames',
    'lastNames',
    'phone',
    'email',
    'birthDate',
    'department',
    'province',
    'district',
    'address',
  ],
  2: ['bank', 'accountNumber', 'cci'],
};

interface LaborDoc {
  file: File | null;
  issueDate: string;
  expiryDate: string;
}
const EMPTY_DOC: LaborDoc = { file: null, issueDate: '', expiryDate: '' };

type FieldErrors = Partial<Record<string, string>>;

/**
 * ⚠️ Estos tres componentes van A NIVEL DE MÓDULO, no dentro de `Registro`.
 *
 * Estuvieron declarados adentro y el formulario era inusable: al teclear un carácter el foco
 * saltaba fuera del campo y había que volver a hacer clic para escribir el siguiente. La causa no
 * se ve leyendo el JSX. Una función declarada dentro de un componente se vuelve a crear en cada
 * render, así que su identidad cambia; React compara tipos por identidad, concluye que es otro
 * componente, **desmonta el subárbol y monta uno nuevo**, y el `<input>` del DOM que tenía el foco
 * deja de existir.
 *
 * Regla: un componente nunca se declara dentro de otro. Si necesita estado del padre, se lo pasa
 * por props — que es lo que hace `CamposProps`.
 */
interface CamposProps {
  form: ApplicationInput;
  errors: FieldErrors;
  setField: (key: keyof ApplicationInput, value: string) => void;
}

function Input({
  name,
  label,
  hint,
  type = 'text',
  form,
  errors,
  setField,
}: {
  name: keyof ApplicationInput;
  label: string;
  hint?: string;
  type?: string;
} & CamposProps) {
  return (
    <div className="mb-3">
      <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor={name}>
        {label}
        {hint && <span className="ml-1 font-normal text-gray-400">({hint})</span>}
      </label>
      <input
        id={name}
        type={type}
        value={form[name]}
        onChange={(e) => setField(name, e.target.value)}
        className={`w-full rounded border px-3 py-2 text-sm ${
          errors[name] ? 'border-red-400' : 'border-gray-300'
        }`}
      />
      {errors[name] && <p className="mt-1 text-xs text-red-600">{errors[name]}</p>}
    </div>
  );
}

/**
 * Banco: desplegable con la lista, más «Otro» que abre un campo escrito.
 *
 * Va A NIVEL DE MÓDULO y su estado («¿está en modo Otro?») vive en `Registro` y llega por props,
 * por la razón que explica el comentario grande de arriba: un componente declarado dentro de otro
 * se remonta en cada render y el campo pierde el foco al teclear.
 *
 * `form.bank` guarda SIEMPRE el valor final —el de la lista, o lo que la persona escribió—, así que
 * quien lee el formulario no necesita saber nada de este modo.
 */
function CampoBanco({
  form,
  errors,
  setField,
  otro,
  setOtro,
}: CamposProps & { otro: boolean; setOtro: (v: boolean) => void }) {
  return (
    <div className="mb-3">
      <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="bank">
        ¿En qué banco tienes tu cuenta?
        <span className="ml-1 font-normal text-gray-400">(opcional)</span>
      </label>
      <select
        id="bank"
        value={otro ? BANCO_OTRO : form.bank}
        onChange={(e) => {
          const elegido = e.target.value;
          if (elegido === BANCO_OTRO) {
            // Se limpia el valor: si quedara el banco anterior, al enviar se guardaría ese
            // mientras la persona ve un campo de texto vacío.
            setOtro(true);
            setField('bank', '');
          } else {
            setOtro(false);
            setField('bank', elegido);
          }
        }}
        className={`w-full rounded border bg-white px-3 py-2 text-sm ${
          errors.bank ? 'border-red-400' : 'border-gray-300'
        }`}
      >
        <option value="">— Selecciona tu banco —</option>
        {BANCOS.map((b) => (
          <option key={b.valor} value={b.valor}>
            {b.etiqueta}
          </option>
        ))}
        <option value={BANCO_OTRO}>Otro (lo escribo yo)</option>
      </select>
      {otro && (
        <input
          id="bankOtro"
          type="text"
          value={form.bank}
          onChange={(e) => setField('bank', e.target.value)}
          placeholder="Escribe el nombre de tu banco o caja"
          aria-label="Nombre de tu banco"
          className="mt-2 w-full rounded border border-gray-300 px-3 py-2 text-sm"
        />
      )}
      {errors.bank && <p className="mt-1 text-xs text-red-600">{errors.bank}</p>}
    </div>
  );
}

function FileField({
  id,
  label,
  hint,
  file,
  onPick,
  error,
}: {
  id: string;
  label: string;
  hint?: string;
  file: File | null;
  onPick: (f: File | null) => void;
  error?: string;
}) {
  const [rechazo, setRechazo] = useState<string | null>(null);
  /**
   * Se valida al ELEGIR, no al enviar. Un video o un PDF elegido por error viajaba entero, se
   * guardaba con nombre de foto y recién se descubría cuando el revisor no podía abrirlo.
   *
   * El tipo y el tope son los del bucket (`lib/archivoDeFoto`, espejo de la 0314). El tope se mide
   * sobre el ORIGINAL porque acá se comprime recién al enviar, y si el navegador no logra
   * recodificarlo (un HEIC en Chrome) viaja tal cual. Hasta el 19-oct el tope era 12 MB y el
   * servidor no tenía ninguno; con el servidor en 10, un HEIC de 11 MB pasaba acá y se caía allá.
   */
  async function elegir(f: File | null) {
    setRechazo(null);
    if (!f) { onPick(null); return; }
    const porTipo = rechazoPorTipo(f);
    if (porTipo !== null) {
      setRechazo(porTipo);
      onPick(null);
      return;
    }
    /**
     * Que el archivo se pueda LEER de verdad, acá y no al enviar.
     *
     * `f.name` y `f.size` responden aunque el puntero esté roto, así que sin esto la pantalla
     * pintaba «Listo: 1000081544.jpg» en verde sobre un archivo muerto y el fallo aparecía cinco
     * pasos después como «Failed to fetch». Ver `lib/archivoLegible.ts`.
     */
    if (await archivoIlegible(f)) {
      setRechazo('No se pudo leer esa foto. Vuelve a tomarla y adjúntala de nuevo.');
      onPick(null);
      return;
    }
    const porTamano = rechazoPorTamano(f);
    if (porTamano !== null) {
      setRechazo(porTamano);
      onPick(null);
      return;
    }
    onPick(f);
  }
  return (
    <div className="mb-3">
      <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor={id}>
        {label}
        {hint && <span className="ml-1 font-normal text-gray-400">({hint})</span>}
      </label>
      <input
        id={id}
        type="file"
        accept="image/*"
        onChange={(e: ChangeEvent<HTMLInputElement>) => void elegir(e.target.files?.[0] ?? null)}
        className={`w-full rounded border px-3 py-2 text-sm ${
          error ? 'border-red-400' : 'border-gray-300'
        }`}
      />
      {file && <p className="mt-1 text-xs text-green-700">Listo: {file.name}</p>}
      {(rechazo ?? error) && <p className="mt-1 text-xs text-red-600">{rechazo ?? error}</p>}
    </div>
  );
}

function LaborDocFields({
  name,
  title,
  doc,
  setDoc,
  errors,
}: {
  name: string;
  title: string;
  doc: LaborDoc;
  setDoc: (d: LaborDoc) => void;
  errors: FieldErrors;
}) {
  return (
    <fieldset className="mb-4 rounded border border-gray-200 p-3">
      <legend className="px-1 text-sm font-semibold text-gray-800">{title}</legend>
      <FileField
        id={`${name}-file`}
        label="Archivo (foto o escaneo)"
        file={doc.file}
        onPick={(f) => setDoc({ ...doc, file: f })}
        error={errors[`${name}File`]}
      />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor={`${name}-issue`}>
            Fecha de emisión
          </label>
          <input
            id={`${name}-issue`}
            type="date"
            value={doc.issueDate}
            onChange={(e) => setDoc({ ...doc, issueDate: e.target.value })}
            className={`w-full rounded border px-3 py-2 text-sm ${
              errors[`${name}Issue`] ? 'border-red-400' : 'border-gray-300'
            }`}
          />
          {errors[`${name}Issue`] && (
            <p className="mt-1 text-xs text-red-600">{errors[`${name}Issue`]}</p>
          )}
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor={`${name}-expiry`}>
            Fecha de vencimiento
          </label>
          <input
            id={`${name}-expiry`}
            type="date"
            value={doc.expiryDate}
            onChange={(e) => setDoc({ ...doc, expiryDate: e.target.value })}
            className={`w-full rounded border px-3 py-2 text-sm ${
              errors[`${name}Expiry`] ? 'border-red-400' : 'border-gray-300'
            }`}
          />
          {errors[`${name}Expiry`] && (
            <p className="mt-1 text-xs text-red-600">{errors[`${name}Expiry`]}</p>
          )}
        </div>
      </div>
    </fieldset>
  );
}

export default function Registro() {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [form, setForm] = useState<ApplicationInput>(EMPTY_FORM);
  const [photo, setPhoto] = useState<File | null>(null);
  const [docFront, setDocFront] = useState<File | null>(null);
  const [docBack, setDocBack] = useState<File | null>(null);
  const [sanidad, setSanidad] = useState<LaborDoc>(EMPTY_DOC);
  const [cul, setCul] = useState<LaborDoc>(EMPTY_DOC);
  const [errors, setErrors] = useState<FieldErrors>({});
  /**
   * ¿El banco se está escribiendo a mano? Se arranca deducido del valor que ya haya: así, si el
   * formulario se rehidrata con un banco que no está en la lista, el campo de texto aparece
   * abierto con su valor en vez de mostrar el desplegable en blanco y perderlo al enviar.
   */
  const [bancoOtro, setBancoOtro] = useState(
    () => form.bank.trim() !== '' && !esBancoDeLaLista(form.bank),
  );
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  function setField(key: keyof ApplicationInput, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  /** Valida solo los campos del paso actual usando el schema completo. */
  function stepErrors(current: 1 | 2): FieldErrors {
    const parsed = applicationSchema.safeParse(form);
    if (parsed.success) return {};
    const keys = STEP_FIELDS[current] as string[];
    const found: FieldErrors = {};
    for (const issue of parsed.error.issues) {
      const key = String(issue.path[0]);
      if (keys.includes(key) && !found[key]) found[key] = issue.message;
    }
    return found;
  }

  async function goToStep2() {
    const found = stepErrors(1);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setGlobalError(null);
    try {
      const status = await documentStatus(form.dni);
      if (status === 'bloqueado') {
        setGlobalError('Este documento está bloqueado. Comunícate con el jefe de operaciones.');
        return;
      }
      if (status === 'pendiente') {
        setGlobalError(
          'Ya tienes un registro pendiente con este documento. Espera a que sea revisado.',
        );
        return;
      }
      if (status === 'registrado') {
        setGlobalError('Este documento ya tiene un usuario creado. Ingresa con tu correo y clave.');
        return;
      }
    } catch {
      // Si la verificación previa falla no bloqueamos: el insert final vuelve a validar.
    }
    setStep(2);
  }

  function goToStep3() {
    const found = stepErrors(2);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setGlobalError(null);
    setStep(3);
  }

  function docErrors(name: string, doc: LaborDoc): FieldErrors {
    const started = Boolean(doc.file || doc.issueDate || doc.expiryDate);
    if (!started) return {};
    const found: FieldErrors = {};
    if (!doc.file) found[`${name}File`] = 'Adjunta el archivo del documento.';
    if (!doc.issueDate) found[`${name}Issue`] = 'Ingresa la fecha de emisión.';
    if (!doc.expiryDate) found[`${name}Expiry`] = 'Ingresa la fecha de vencimiento.';
    if (doc.issueDate && doc.expiryDate && doc.expiryDate <= doc.issueDate) {
      found[`${name}Expiry`] = 'El vencimiento debe ser posterior a la emisión.';
    }
    return found;
  }

  async function onSubmit() {
    // Lo PRIMERO: un error del intento anterior no puede seguir en rojo mientras el postulante
    // corrige. Antes se limpiaba recién después de pasar la validación, así que quien fallaba y
    // luego olvidaba un archivo seguía leyendo el error viejo, que ya no era cierto.
    setGlobalError(null);
    const parsed = applicationSchema.safeParse(form);
    const found: FieldErrors = {};
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0]);
        if (!found[key]) found[key] = issue.message;
      }
    }
    if (!photo) found.photo = 'Adjunta tu foto personal.';
    if (!docFront) found.docFront = 'Adjunta la foto frontal de tu documento.';
    if (!docBack) found.docBack = 'Adjunta la foto posterior de tu documento.';
    Object.assign(found, docErrors('sanidad', sanidad), docErrors('cul', cul));
    setErrors(found);
    if (Object.keys(found).length > 0 || !parsed.success) {
      if (!parsed.success) setGlobalError('Revisa los pasos anteriores: hay datos incompletos.');
      return;
    }

    /**
     * Se vuelve a comprobar que TODOS los archivos se puedan leer, antes de subir el primer byte.
     *
     * No es redundante con la comprobación de `FileField`: entre elegir la foto y apretar «Enviar
     * registro» pasan minutos, y es JUSTO en ese rato que Android recicla el archivo temporal de la
     * cámara. El caso real del 9-set: tres archivos subidos y el cuarto muerto, cuarenta veces
     * seguidas —porque reenviar reusa el mismo `File`—.
     *
     * Y se LIMPIA el campo del archivo muerto: si se dejara puesto, la persona volvería a apretar
     * «Enviar» con el mismo archivo roto y volvería a fallar para siempre. Vaciarlo es lo que
     * convierte «error eterno» en «vuelve a tomar esta foto».
     */
    const adjuntos: { archivo: File | null; etiqueta: string; limpiar: () => void }[] = [
      { archivo: photo, etiqueta: 'tu foto personal', limpiar: () => setPhoto(null) },
      { archivo: docFront, etiqueta: 'el documento (lado frontal)', limpiar: () => setDocFront(null) },
      { archivo: docBack, etiqueta: 'el documento (lado posterior)', limpiar: () => setDocBack(null) },
      { archivo: sanidad.file, etiqueta: 'el carné de sanidad',
        limpiar: () => setSanidad({ ...sanidad, file: null }) },
      { archivo: cul.file, etiqueta: 'el certificado único laboral',
        limpiar: () => setCul({ ...cul, file: null }) },
    ];
    const muertos: string[] = [];
    for (const a of adjuntos) {
      if (a.archivo === null) continue;
      if (await archivoIlegible(a.archivo)) { muertos.push(a.etiqueta); a.limpiar(); }
    }
    if (muertos.length > 0) {
      setGlobalError(
        `Tu teléfono ya no encuentra ${muertos.join(' ni ')}. Pasa cuando la foto se tomó hace `
        + 'un rato y el celular la borró de la memoria temporal. Vuelve a tomar '
        + `${muertos.length === 1 ? 'esa foto' : 'esas fotos'} y adjuntar${muertos.length === 1 ? 'la' : 'las'} `
        + 'de nuevo: el resto de tus datos quedó escrito.',
      );
      return;
    }

    setSending(true);
    try {
      const dni = parsed.data.dni;
      setProgress('Subiendo tu foto personal…');
      const photoPath = await uploadApplicationFile(dni, 'foto', photo!);
      setProgress('Subiendo tu documento (frontal)…');
      const docFrontPath = await uploadApplicationFile(dni, 'documento-frontal', docFront!);
      setProgress('Subiendo tu documento (posterior)…');
      const docBackPath = await uploadApplicationFile(dni, 'documento-posterior', docBack!);

      const documents: WorkerDocumentInput[] = [];
      if (sanidad.file) {
        setProgress('Subiendo tu carné de sanidad…');
        documents.push({
          type: 'carnet_sanidad',
          path: await uploadApplicationDocument(dni, 'carnet_sanidad', sanidad.file),
          issue_date: sanidad.issueDate,
          expiry_date: sanidad.expiryDate,
        });
      }
      if (cul.file) {
        setProgress('Subiendo tu certificado único laboral…');
        documents.push({
          type: 'certificado_unico_laboral',
          path: await uploadApplicationDocument(dni, 'certificado_unico_laboral', cul.file),
          issue_date: cul.issueDate,
          expiry_date: cul.expiryDate,
        });
      }

      setProgress('Enviando tu registro…');
      await submitApplication({
        ...parsed.data,
        photoPath,
        docFrontPath,
        docBackPath,
        documents,
      });
      setSent(true);
    } catch (err) {
      if (
        err instanceof DuplicatePendingError ||
        err instanceof BlockedDocumentError ||
        err instanceof AlreadyRegisteredError
      ) {
        setGlobalError(err.message);
      } else {
        setGlobalError(
          err instanceof Error
            ? err.message
            : 'No se pudo enviar tu registro. Revisa tu conexión e inténtalo de nuevo.',
        );
      }
    } finally {
      setProgress(null);
      setSending(false);
    }
  }

  /** Lo que los campos necesitan del formulario. Va por props porque los componentes viven fuera. */
  const campos: CamposProps = { form, errors, setField };

  if (sent) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md rounded-lg bg-white p-8 text-center shadow">
          <h1 className="text-lg font-bold text-gray-900">Registro enviado</h1>
          <p className="mt-2 text-sm text-gray-600">
            Recibimos tus datos y tus documentos. El equipo de operaciones los revisará y te
            enviaremos tu correo y clave de acceso cuando estés aprobado.
          </p>
          <Link to="/login" className="mt-4 inline-block text-sm text-blue-600 underline">
            Ir al inicio de sesión
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen justify-center bg-gray-50 px-4 py-8">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow">
        <h1 className="text-xl font-bold text-gray-900">Registro de trabajador</h1>
        <p className="mb-4 text-sm text-gray-500">
          Paso {step} de 3 —{' '}
          {step === 1 ? 'Datos personales' : step === 2 ? 'Datos de pago' : 'Documentos'}
        </p>
        <div className="mb-5 h-1 w-full rounded bg-gray-200">
          <div
            className="h-1 rounded bg-blue-600 transition-all"
            style={{ width: `${(step / 3) * 100}%` }}
          />
        </div>

        {step === 1 && (
          <>
            <div className="mb-3">
              <label
                className="mb-1 block text-sm font-medium text-gray-700"
                htmlFor="documentType"
              >
                Tipo de documento
              </label>
              <select
                id="documentType"
                value={form.documentType}
                onChange={(e) => setField('documentType', e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="DNI">DNI</option>
                <option value="CE">Carné de extranjería</option>
              </select>
            </div>
            <Input
              name="dni"
              label="Número de documento"
              hint={form.documentType === 'DNI' ? '8 dígitos' : '9 a 12 caracteres'}
              {...campos}
            />
            <Input name="firstNames" label="Nombres" {...campos} />
            <Input name="lastNames" label="Apellidos" {...campos} />
            <Input name="phone" label="Celular" hint="9 dígitos, empieza en 9" {...campos} />
            <Input name="email" label="Correo" type="email" {...campos} />
            <Input name="birthDate" label="Fecha de nacimiento" type="date" {...campos} />
            <Input name="department" label="Departamento" {...campos} />
            <Input name="province" label="Provincia" {...campos} />
            <Input name="district" label="Distrito" {...campos} />
            <Input name="address" label="Dirección (opcional)" {...campos} />
            {globalError && <p className="mb-3 text-sm text-red-600">{globalError}</p>}
            <button
              type="button"
              onClick={goToStep2}
              className="w-full rounded bg-blue-600 py-2 text-sm font-medium text-white"
            >
              Siguiente
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <p className="mb-3 text-xs text-gray-500">
              Estos datos se usan solo para pagarte. Puedes dejarlos vacíos y completarlos después.
            </p>
            <CampoBanco {...campos} otro={bancoOtro} setOtro={setBancoOtro} />
            <Input name="accountNumber" label="Nro. de cuenta (opcional)" {...campos} />
            <Input name="cci" label="CCI (opcional)" hint="20 dígitos" {...campos} />
            {globalError && <p className="mb-3 text-sm text-red-600">{globalError}</p>}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="w-1/3 rounded border border-gray-300 py-2 text-sm text-gray-700"
              >
                Atrás
              </button>
              <button
                type="button"
                onClick={goToStep3}
                className="w-2/3 rounded bg-blue-600 py-2 text-sm font-medium text-white"
              >
                Siguiente
              </button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <FileField
              id="photo"
              label="Foto personal"
              hint="rostro visible"
              file={photo}
              onPick={setPhoto}
              error={errors.photo}
            />
            <FileField
              id="docFront"
              label="Documento — lado frontal"
              file={docFront}
              onPick={setDocFront}
              error={errors.docFront}
            />
            <FileField
              id="docBack"
              label="Documento — lado posterior"
              file={docBack}
              onPick={setDocBack}
              error={errors.docBack}
            />
            <p className="mb-3 text-xs text-gray-500">
              Documentos laborales: si aún no los tienes, puedes entregarlos después. Si adjuntas
              uno, completa sus dos fechas.
            </p>
            <LaborDocFields
              name="sanidad"
              title="Carné de sanidad"
              doc={sanidad}
              setDoc={setSanidad}
              errors={errors}
            />
            <LaborDocFields
              name="cul"
              title="Certificado único laboral"
              doc={cul}
              setDoc={setCul}
              errors={errors}
            />
            {progress && <p className="mb-2 text-sm text-blue-700">{progress}</p>}
            {globalError && <p className="mb-3 text-sm text-red-600">{globalError}</p>}
            <div className="flex gap-2">
              <button
                type="button"
                disabled={sending}
                onClick={() => setStep(2)}
                className="w-1/3 rounded border border-gray-300 py-2 text-sm text-gray-700 disabled:opacity-50"
              >
                Atrás
              </button>
              <button
                type="button"
                disabled={sending}
                onClick={onSubmit}
                className="w-2/3 rounded bg-blue-600 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {sending ? 'Enviando…' : 'Enviar registro'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
